import { useRef, useState, useEffect, useCallback } from "react";
import { cn } from "@githits/shared";
import { LanguageIcon } from "@githits/replay-shared";

const LANGUAGES = [
  { key: "python", label: "Python" },
  { key: "typescript", label: "TypeScript" },
  { key: "rust", label: "Rust" },
  { key: "go", label: "Go" },
  { key: "java", label: "Java" },
  { key: "elixir", label: "Elixir" },
  { key: "cuda", label: "CUDA" },
  { key: "csharp", label: "C#" },
] as const;

const SCROLL_AMOUNT = 150;

interface LanguageTabsProps {
  activeLanguage: string;
  onSelect: (language: string) => void;
}

export default function LanguageTabs({
  activeLanguage,
  onSelect,
}: LanguageTabsProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Auto-scroll to keep the active tab fully visible
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const index = LANGUAGES.findIndex((l) => l.key === activeLanguage);
    const tab = tabRefs.current[index];
    if (!tab) return;
    // Small delay so the scroll happens after any browser-native click scroll
    requestAnimationFrame(() => {
      tab.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
    });
  }, [activeLanguage]);

  const updateChevrons = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    updateChevrons();
    el.addEventListener("scroll", updateChevrons);
    window.addEventListener("resize", updateChevrons);
    return () => {
      el.removeEventListener("scroll", updateChevrons);
      window.removeEventListener("resize", updateChevrons);
    };
  }, [updateChevrons]);

  const scroll = (direction: -1 | 1) => {
    listRef.current?.scrollBy({
      left: direction * SCROLL_AMOUNT,
      behavior: "smooth",
    });
  };

  const chevron = (direction: -1 | 1) => {
    const enabled = direction === -1 ? canScrollLeft : canScrollRight;
    const points = direction === -1 ? "15 18 9 12 15 6" : "9 18 15 12 9 6";
    const label = direction === -1 ? "Scroll left" : "Scroll right";
    return (
      <button
        type="button"
        className="hero-chevron ghw-shrink-0"
        onClick={() => scroll(direction)}
        disabled={!enabled}
        aria-label={label}
        style={{
          background: "none",
          border: "none",
          padding: 4,
          marginLeft: direction === -1 ? -4 : 0,
          marginRight: direction === 1 ? -4 : 0,
          color: "#abb2bf",
          cursor: enabled ? "pointer" : "not-allowed",
          opacity: enabled ? 1 : 0.3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "color 200ms",
        }}
        onMouseEnter={(e) => {
          if (enabled) e.currentTarget.style.color = "#ffffff";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "#abb2bf";
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points={points} />
        </svg>
      </button>
    );
  };

  return (
    <div
      className="ghw-flex ghw-items-center ghw-shrink-0"
      style={{ gap: 4, minWidth: 0 }}
    >
      {chevron(-1)}

      {/* Scrollable tab list */}
      <div
        ref={listRef}
        role="tablist"
        className="ghw-flex ghw-items-center ghw-flex-1 hero-tabs-scroll"
        style={{
          gap: 8,
          minWidth: 0,
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
      >
        {LANGUAGES.map(({ key, label }, i) => (
          <button
            key={key}
            ref={(el) => { tabRefs.current[i] = el; }}
            type="button"
            role="tab"
            aria-selected={activeLanguage === key}
            tabIndex={activeLanguage === key ? 0 : -1}
            onClick={() => onSelect(key)}
            onKeyDown={(e) => {
              let next = -1;
              if (e.key === "ArrowRight") next = (i + 1) % LANGUAGES.length;
              else if (e.key === "ArrowLeft") next = (i - 1 + LANGUAGES.length) % LANGUAGES.length;
              else if (e.key === "Home") next = 0;
              else if (e.key === "End") next = LANGUAGES.length - 1;
              if (next >= 0) {
                e.preventDefault();
                onSelect(LANGUAGES[next].key);
                tabRefs.current[next]?.focus();
              }
            }}
            className={cn(
              "ghw-flex ghw-items-center ghw-shrink-0 ghw-whitespace-nowrap",
            )}
            style={{
              gap: 8,
              padding: 8,
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: 12,
              lineHeight: "12px",
              color: activeLanguage === key ? "#ffffff" : "#abb2bf",
              background: activeLanguage === key ? "#4a4e56" : "transparent",
              outline: "none",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                filter: activeLanguage === key ? "none" : "grayscale(100%)",
                opacity: activeLanguage === key ? 1 : 0.5,
                borderRadius: 4,
              }}
              className="hero-icon-no-ring"
            >
              <LanguageIcon
                language={key}
                label={label}
                isActive={activeLanguage === key}
              />
            </span>
            {label}
          </button>
        ))}
      </div>

      {chevron(1)}
    </div>
  );
}

export { LANGUAGES };
